import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { faFile, faTimes, faFilePdf, faFileVideo, faFileImage, faFileAudio } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import get from 'lodash-es/get';
import isString from 'lodash-es/isString';
import prettyBytes from 'pretty-bytes';
import PropTypes from 'prop-types';
import React, { useMemo, useCallback } from 'react';
import Button from '@panneau/element-button';
import Icon from '@panneau/element-icon';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import isArray from 'lodash-es/isArray';

var propTypes$1 = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.shape({
    filename: PropTypes.string,
    size: PropTypes.number,
    url: PropTypes.string
  })]),
  vertical: PropTypes.bool,
  index: PropTypes.number,
  namePath: PropTypes.string,
  thumbnailPath: PropTypes.string,
  sizePath: PropTypes.string,
  linkPath: PropTypes.string,
  maxWidth: PropTypes.number,
  maxHeight: PropTypes.number,
  thumbnailSize: PropTypes.number,
  disabled: PropTypes.bool,
  actionsDisabled: PropTypes.bool,
  withoutDescription: PropTypes.bool,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  external: PropTypes.bool,
  onClick: PropTypes.func,
  onClickRemove: PropTypes.func,
  onClickDescription: PropTypes.func,
  className: PropTypes.string,
  cardClassName: PropTypes.string,
  children: PropTypes.node
};
var defaultProps$1 = {
  value: null,
  vertical: false,
  index: null,
  namePath: 'name',
  thumbnailPath: 'thumbnail_url',
  sizePath: 'metadata.size',
  linkPath: null,
  disabled: false,
  actionsDisabled: false,
  maxWidth: null,
  maxHeight: null,
  thumbnailSize: 100,
  withoutDescription: false,
  selectable: false,
  selected: false,
  external: true,
  onClick: null,
  onClickRemove: null,
  onClickDescription: null,
  className: null,
  cardClassName: null,
  children: null
};
var MediaCard = function MediaCard(_ref) {
  var initialValue = _ref.value,
    vertical = _ref.vertical,
    index = _ref.index,
    namePath = _ref.namePath,
    thumbnailPath = _ref.thumbnailPath,
    sizePath = _ref.sizePath,
    linkPath = _ref.linkPath,
    maxWidth = _ref.maxWidth,
    maxHeight = _ref.maxHeight,
    thumbnailSize = _ref.thumbnailSize,
    disabled = _ref.disabled,
    actionsDisabled = _ref.actionsDisabled,
    withoutDescription = _ref.withoutDescription,
    selectable = _ref.selectable,
    selected = _ref.selected,
    external = _ref.external,
    onClick = _ref.onClick,
    onClickRemove = _ref.onClickRemove,
    onClickDescription = _ref.onClickDescription,
    className = _ref.className,
    cardClassName = _ref.cardClassName,
    children = _ref.children;
  var value = initialValue || {};
  var _ref2 = value || {},
    _ref2$id = _ref2.id,
    id = _ref2$id === void 0 ? null : _ref2$id,
    _ref2$filename = _ref2.filename,
    filename = _ref2$filename === void 0 ? null : _ref2$filename,
    _ref2$size = _ref2.size,
    fileSize = _ref2$size === void 0 ? 0 : _ref2$size,
    _ref2$thumbnail_url = _ref2.thumbnail_url,
    thumbnailUrl = _ref2$thumbnail_url === void 0 ? null : _ref2$thumbnail_url,
    _ref2$thumbnailUrl = _ref2.thumbnailUrl,
    altThumbnailUrl = _ref2$thumbnailUrl === void 0 ? null : _ref2$thumbnailUrl,
    _ref2$preview = _ref2.preview,
    preview = _ref2$preview === void 0 ? null : _ref2$preview,
    _ref2$data = _ref2.data,
    data = _ref2$data === void 0 ? {} : _ref2$data,
    type = _ref2.type;
  var _ref3 = data || {},
    _ref3$file = _ref3.file,
    file = _ref3$file === void 0 ? null : _ref3$file;
  var mediaIcon = useMemo(function () {
    var faIcon = null;
    switch (type) {
      case 'audio':
        faIcon = faFileAudio;
        break;
      case 'image':
        faIcon = faFileImage;
        break;
      case 'video':
        faIcon = faFileVideo;
        break;
      case 'document':
        faIcon = faFilePdf;
        break;
      default:
        faIcon = faFile;
        break;
    }
    return faIcon;
  }, [type]);
  var _useMemo = useMemo(function () {
      var finalName = (namePath !== null ? get(value, namePath) : null) || filename || (isString(file) ? file : null) || null;
      var finalThumbnail = (thumbnailPath !== null ? get(value, thumbnailPath) : null) || thumbnailUrl || altThumbnailUrl || preview;
      var finalSize = (sizePath !== null ? get(value, sizePath) : null) || fileSize || null;
      var finalLink = (linkPath !== null ? get(value, linkPath) || null : null) || null;
      var finalHasThumbnail = finalThumbnail !== null;
      return {
        name: finalName,
        thumbnail: finalThumbnail,
        size: finalSize,
        link: finalLink,
        hasThumbnail: finalHasThumbnail
      };
    }, [value, namePath, thumbnailPath, sizePath, linkPath, thumbnailUrl, altThumbnailUrl, filename, file, preview]),
    name = _useMemo.name,
    thumbnail = _useMemo.thumbnail,
    size = _useMemo.size,
    link = _useMemo.link,
    hasThumbnail = _useMemo.hasThumbnail;
  var withButton = onClick !== null || link !== null;
  var thumbnailElement = hasThumbnail || mediaIcon !== null ? /*#__PURE__*/React.createElement("div", {
    className: "p-2 d-flex align-items-center justify-content-center",
    style: hasThumbnail ? {
      width: !withoutDescription && !vertical ? thumbnailSize : '100%',
      height: !withoutDescription ? thumbnailSize : '100%',
      background: 'repeating-conic-gradient(color-mix(in srgb, var(--bs-body-bg), #000 15%) 0% 25%, color-mix(in srgb, var(--bs-body-bg), #FFF 15%) 0% 50%) 50% / 20px 20px'
    } : {
      width: !withoutDescription && !vertical ? thumbnailSize : '100%',
      height: !withoutDescription ? thumbnailSize : '100%'
    }
  }, !hasThumbnail && mediaIcon !== null ? /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: mediaIcon,
    className: "m-auto fs-3"
  }) : null, hasThumbnail && thumbnail !== null ? /*#__PURE__*/React.createElement("img", {
    className: classNames(['img-fluid', {
      'mw-100': maxWidth === null,
      'mh-100': maxHeight === null
    }]),
    src: thumbnail,
    alt: name,
    style: {
      maxHeight: !withoutDescription ? thumbnailSize : thumbnailSize * 1.5
    }
  }) : null, hasThumbnail && thumbnail === null ? /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center justify-content-center mh-100 mw-100",
    style: {
      minHeight: !withoutDescription ? thumbnailSize : thumbnailSize * 1.5
    }
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faFile,
    className: "m-auto fs-3"
  })) : null) : null;
  var onClickThumbnail = useCallback(function () {
    if (onClick !== null) {
      onClick(value);
    }
  }, [value, onClick]);
  var descriptionElement = /*#__PURE__*/React.createElement("div", {
    className: classNames(['card-body', {
      'p-2': vertical
    }])
  }, /*#__PURE__*/React.createElement("h5", {
    className: classNames(['card-title', 'text-break', 'fs-6', {
      'mb-1': size !== null && size > 0,
      'mb-0': size === null || size <= 0
    }])
  }, link !== null ? /*#__PURE__*/React.createElement("a", Object.assign({
    href: link
  }, external ? {
    target: '_blank',
    rel: 'noopener noreferrer'
  } : null), name) : name), size !== null && size > 0 ? /*#__PURE__*/React.createElement("p", {
    className: "card-text text-muted small"
  }, prettyBytes(size), onClickDescription !== null && link === null ? /*#__PURE__*/React.createElement(Icon, {
    className: "icon ms-1",
    name: "pencil"
  }) : null) : null, children);
  var onClickBody = useCallback(function () {
    if (onClickDescription !== null) {
      onClickDescription(value);
    }
  }, [value, onClick]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames(['w-100', 'mb-1', _defineProperty({}, className, className !== null)])
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(['card', 'mb-1', _defineProperty({
      'mw-100': maxWidth === null
    }, cardClassName, cardClassName !== null)]),
    key: "media-".concat(id),
    style: {
      maxWidth: maxWidth,
      maxHeight: maxHeight
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(['d-flex align-items-center position-relative', {
      'flex-column': vertical,
      'text-center': vertical,
      'flex-grow-1': !vertical
    }])
  }, withButton ? /*#__PURE__*/React.createElement(Button, {
    className: classNames(['btn', 'p-0', {
      'w-100': withoutDescription || vertical
    }]),
    type: "button",
    href: link,
    external: external,
    target: external ? '_blank' : null,
    onClick: onClickThumbnail
    // withoutStyle
    ,
    style: {
      border: selected ? '1px solid var(--bs-primary-border-subtle)' : '1px solid transparent',
      backgroundColor: selected ? 'var(--bs-focus-ring-color)' : null,
      borderRadius: 'var(--bs-border-radius)',
      borderTopRightRadius: !vertical ? 0 : 'var(--bs-border-radius)',
      borderBottomRightRadius: 0,
      borderBottomLeftRadius: !vertical ? 0 : 'inherit',
      overflow: 'hidden',
      minWidth: !vertical && thumbnailElement !== null ? thumbnailSize + 10 : null
    }
  }, thumbnailElement || 'Thumbnail', selectable ? /*#__PURE__*/React.createElement("input", {
    className: "position-absolute",
    type: "checkbox",
    checked: selected,
    style: {
      top: 10,
      right: 10
    }
  }) : null) : thumbnailElement, !withoutDescription && link === null && onClickDescription !== null && !actionsDisabled ? /*#__PURE__*/React.createElement(Button, {
    className: "flex-grow-1 w-100",
    onClick: onClickBody
  }, descriptionElement) : null, !withoutDescription && (onClickDescription === null || actionsDisabled) ? /*#__PURE__*/React.createElement("div", {
    className: "flex-grow-1 w-100"
  }, descriptionElement) : null, onClickRemove !== null ? /*#__PURE__*/React.createElement("div", {
    className: "p-2"
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    size: "sm",
    theme: "secondary",
    outline: true,
    onClick: function onClick() {
      return onClickRemove(index, value);
    },
    disabled: disabled
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faTimes
  }))) : null)));
};
MediaCard.propTypes = propTypes$1;
MediaCard.defaultProps = defaultProps$1;

var styles = {"mediaCards":"panneau-element-media-card-mediaCards"};

var _excluded = ["value", "className", "cardClassName"];
var propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.shape({
    filename: PropTypes.string,
    size: PropTypes.number,
    url: PropTypes.string
  })]),
  className: PropTypes.string,
  cardClassName: PropTypes.string
};
var defaultProps = {
  value: null,
  className: null,
  cardClassName: null
};
var MediaCards = function MediaCards(_ref) {
  var value = _ref.value,
    className = _ref.className,
    cardClassName = _ref.cardClassName,
    props = _objectWithoutProperties(_ref, _excluded);
  var values = useMemo(function () {
    if (isArray(value)) {
      return value;
    }
    return value !== null ? [value] : [];
  }, [value]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.mediaCards, _defineProperty({}, className, className !== null)])
  }, values.map(function (media, idx) {
    return /*#__PURE__*/React.createElement(MediaCard, Object.assign({
      key: "media-card-".concat(idx + 1, "-").concat(media !== null ? media === null || media === void 0 ? void 0 : media.id : null),
      className: classNames([styles.card, _defineProperty({}, cardClassName, cardClassName !== null)])
    }, props, {
      value: media,
      index: idx
    }));
  }));
};
MediaCards.propTypes = propTypes;
MediaCards.defaultProps = defaultProps;

export { MediaCards, MediaCard as default };
